/**
 * Allys Combined Contact Section Component
 * 
 * Clean side-by-side layout with contact information on the left and form on the right
 * Following Allys theme styling with Inter font and sophisticated design
 */

'use client';

import { memo } from 'react';
import { ContactFormSection, type ContactFormField } from '@/components/contact/ContactFormSection';
import { Envelope, Phone, MapPin, Clock, CurrencyDollar } from '@phosphor-icons/react';
import { AllysPhoneInput } from './AllysPhoneInput';
import { AllysCurrencyInput } from './AllysCurrencyInput';

// Helper to safely get string values from messages
const safeGetString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  return fallback;
};

// Helper to safely get object values
const safeGetObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  return {};
};

export interface AllysContactSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
  onSubmit?: (data: Record<string, string>) => Promise<void>;
}

export const AllysContactSection = memo(function AllysContactSection({
  messages,
  locale: _locale,
  onSubmit
}: AllysContactSectionProps) {
  const contactMessages = messages?.contact as Record<string, unknown> | undefined;
  const infoMessages = safeGetObject(contactMessages?.info);
  const formMessages = safeGetObject(contactMessages?.form);
  const propertyTypeOptions = safeGetObject(formMessages?.propertyTypeOptions);
  const whyChooseMsg = safeGetObject(contactMessages?.whyChoose);

  const fields: ContactFormField[] = [
    {
      name: 'firstName',
      label: safeGetString(formMessages?.firstName, 'First Name'),
      placeholder: safeGetString(formMessages?.firstNamePlaceholder, 'John'),
      type: 'text',
      required: true,
    },
    {
      name: 'lastName',
      label: safeGetString(formMessages?.lastName, 'Last Name'),
      placeholder: safeGetString(formMessages?.lastNamePlaceholder, 'Doe'),
      type: 'text',
      required: true,
    },
    {
      name: 'email',
      label: safeGetString(formMessages?.email, 'Email Address'),
      placeholder: safeGetString(formMessages?.emailPlaceholder, 'john@example.com'),
      type: 'email',
      required: true,
    },
    {
      name: 'phone',
      label: safeGetString(formMessages?.phone, 'Phone Number'),
      placeholder: safeGetString(formMessages?.phonePlaceholder, '+230 XXXX XXXX'),
      type: 'tel',
      required: false,
    },
    {
      name: 'propertyType',
      label: safeGetString(formMessages?.propertyType, 'Property Type'),
      placeholder: safeGetString(formMessages?.propertyTypePlaceholder, 'Select property type'),
      type: 'select',
      required: false,
      options: [
        { value: 'residential', label: safeGetString(propertyTypeOptions?.residential, 'Residential') },
        { value: 'commercial', label: safeGetString(propertyTypeOptions?.commercial, 'Commercial') },
        { value: 'land', label: safeGetString(propertyTypeOptions?.land, 'Land') },
        { value: 'luxury', label: safeGetString(propertyTypeOptions?.luxury, 'Luxury Villa') },
        { value: 'apartment', label: safeGetString(propertyTypeOptions?.apartment, 'Apartment') },
        { value: 'other', label: safeGetString(propertyTypeOptions?.other, 'Other') },
      ],
    },
    {
      name: 'budget',
      label: safeGetString(formMessages?.budget, 'Budget Range'),
      placeholder: safeGetString(formMessages?.budgetPlaceholder, 'e.g., $500,000 - $1,000,000'),
      type: 'text',
      required: false,
    },
    {
      name: 'message',
      label: safeGetString(formMessages?.message, 'Your Message'),
      placeholder: safeGetString(formMessages?.messagePlaceholder, 'Tell us about your property requirements...'),
      type: 'textarea',
      required: true,
    },
  ];

  const contactInfo = [
    {
      icon: <Envelope size={24} weight="light" />,
      label: safeGetString(infoMessages?.emailLabel, 'Email'),
      value: safeGetString(infoMessages?.email, 'contact@allys.mu'),
      href: `mailto:${safeGetString(infoMessages?.email, 'contact@allys.mu')}`,
    },
    {
      icon: <Phone size={24} weight="light" />,
      label: safeGetString(infoMessages?.phoneLabel, 'Phone'),
      value: safeGetString(infoMessages?.phone, '+230 460 0909'),
      href: `tel:${safeGetString(infoMessages?.phone, '+230 460 0909')}`,
    },
    {
      icon: <MapPin size={24} weight="light" />,
      label: safeGetString(infoMessages?.addressLabel, 'Address'),
      value: safeGetString(infoMessages?.address, 'Beau Bassin-Rose Hill, Mauritius'),
    },
    {
      icon: <Clock size={24} weight="light" />,
      label: safeGetString(infoMessages?.hours, 'Office Hours'),
      value: safeGetString(infoMessages?.hoursValue, 'Monday - Friday: 9:00 AM - 5:00 PM'),
      subValue: safeGetString(infoMessages?.hoursSaturday, 'Saturday: 9:00 AM - 1:00 PM'),
    },
  ];

  return (
    <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-normal text-gray-900 mb-4 tracking-tight font-inter"
          >
            {safeGetString(formMessages?.title, 'Get In Touch')}
          </h2>
          <p 
            className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed font-inter"
          >
            {safeGetString(formMessages?.subtitle, "We're here to help you find your dream property")}
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-16 items-start">
          {/* Left Column - Contact Information */}
          <div className="lg:col-span-2 space-y-8 lg:sticky lg:top-28">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="group border-b border-gray-200 last:border-0 pb-6 last:pb-0"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-900 group-hover:bg-gray-900 group-hover:text-white transition-all duration-200">
                      {info.icon}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p 
                        className="text-xs font-normal text-gray-500 uppercase tracking-wider mb-2 font-inter"
                      >
                        {info.label}
                      </p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-lg font-normal text-gray-900 hover:text-gray-600 transition-colors duration-200 block break-words font-inter"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p
                          className="text-lg font-normal text-gray-900 break-words font-inter"
                        >
                          {info.value}
                        </p>
                      )}
                      {info.subValue && (
                        <p
                          className="text-base text-gray-600 mt-1 font-light font-inter"
                        >
                          {info.subValue}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Info Section */}
            <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
              <h3 
                className="text-xl font-normal text-gray-900 mb-4 tracking-tight font-inter"
              >
                {safeGetString(whyChooseMsg?.title, 'Why Choose Allys?')}
              </h3>
              <ul className="space-y-3 font-inter">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600 font-light">{safeGetString(whyChooseMsg?.point1, 'Premium property portfolio across Mauritius')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600 font-light">{safeGetString(whyChooseMsg?.point2, 'Expert local market knowledge')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600 font-light">{safeGetString(whyChooseMsg?.point3, 'Personalized client service')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-600 font-light">{safeGetString(whyChooseMsg?.point4, 'Quick response within 24 hours')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 rounded-lg p-8 lg:p-10 border border-gray-200">
              <ContactFormSection
                title=""
                fields={fields}
                submitLabel={safeGetString(formMessages?.submit, 'Send Enquiry')}
                sendingLabel={safeGetString(formMessages?.sending, 'Sending...')}
                successMessage={safeGetString(formMessages?.success, "Thank you! We'll be in touch shortly.")}
                errorMessage={safeGetString(formMessages?.error, 'Error sending message. Please try again.')}
                {...(onSubmit ? { onSubmit } : {})}
                className="allys-combined-contact-form"
                renderField={(field, value, onChange, error) => {
                  // Get icon based on field
                  const getFieldIcon = () => {
                    switch (field.name) {
                      case 'email':
                        return <Envelope size={20} weight="light" className="text-gray-400" />;
                      case 'phone':
                        return <Phone size={20} weight="light" className="text-gray-400" />;
                      case 'budget':
                        return <CurrencyDollar size={20} weight="light" className="text-gray-400" />;
                      case 'propertyType':
                        return <MapPin size={20} weight="light" className="text-gray-400" />;
                      default:
                        return null;
                    }
                  };

                  return (
                    <div className="mb-6">
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-normal text-gray-900 mb-2 font-inter"
                      >
                        {field.label}
                        {field.required && <span className="text-gray-500 ml-1">*</span>}
                      </label>

                      <div className="relative">
                        {field.name === 'phone' ? (
                          <AllysPhoneInput
                            id={field.name}
                            name={field.name}
                            value={value}
                            onChange={onChange}
                            placeholder={field.placeholder}
                            {...(field.required ? { required: field.required } : {})}
                            {...(error ? { error } : {})}
                            defaultCountry="MU"
                          />
                        ) : field.name === 'budget' ? (
                          <AllysCurrencyInput
                            id={field.name}
                            name={field.name}
                            value={value}
                            onChange={onChange}
                            placeholder={field.placeholder}
                            {...(field.required ? { required: field.required } : {})}
                            {...(error ? { error } : {})}
                            defaultCurrency="MUR"
                          />
                        ) : field.type === 'textarea' ? (
                          <textarea
                            id={field.name}
                            name={field.name}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            rows={5}
                            className={`
                              w-full px-4 py-3 
                              border ${error ? 'border-red-500' : 'border-gray-200'} 
                              rounded-lg
                              focus:border-gray-900 focus:outline-none
                              transition-colors duration-200
                              text-gray-900 placeholder:text-gray-400
                              resize-none
                              bg-white font-inter
                            `}
                          />
                        ) : field.type === 'select' ? (
                          <div className="relative">
                            {getFieldIcon() && (
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                                {getFieldIcon()}
                              </div>
                            )}
                            <select
                              id={field.name}
                              name={field.name}
                              value={value}
                              onChange={(e) => onChange(e.target.value)}
                              required={field.required}
                              className={`
                                w-full ${getFieldIcon() ? 'pl-11' : 'pl-4'} pr-10 py-3
                                border ${error ? 'border-red-500' : 'border-gray-200'}
                                rounded-lg
                                focus:border-gray-900 focus:outline-none
                                transition-colors duration-200
                                text-gray-900 placeholder:text-gray-400
                                appearance-none bg-white font-inter
                                cursor-pointer
                              `}
                            >
                              <option value="">{field.placeholder}</option>
                              {field.options?.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        ) : (
                          <div className="relative">
                            {getFieldIcon() && (
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                                {getFieldIcon()}
                              </div>
                            )}
                            <input
                              type={field.type}
                              id={field.name}
                              name={field.name}
                              value={value}
                              onChange={(e) => onChange(e.target.value)}
                              placeholder={field.placeholder}
                              required={field.required}
                              className={`
                                w-full ${getFieldIcon() ? 'pl-11' : 'pl-4'} pr-4 py-3
                                border ${error ? 'border-red-500' : 'border-gray-200'}
                                rounded-lg
                                focus:border-gray-900 focus:outline-none
                                transition-colors duration-200
                                text-gray-900 placeholder:text-gray-400
                                bg-white font-inter
                              `}
                            />
                          </div>
                        )}
                      </div>

                      {/* Error message - only show if not phone or budget field (they have their own error display) */}
                      {error && field.name !== 'phone' && field.name !== 'budget' && (
                        <p className="mt-2 text-sm text-red-500 font-inter">
                          {error}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .allys-combined-contact-form .contact-form-header {
          display: none;
        }

        .allys-combined-contact-form .contact-form-submit {
          width: 100%;
          padding: 0.875rem 1.5rem;
          background: #111827;
          color: white;
          font-weight: 400;
          font-size: 1rem;
          border-radius: 0.5rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .allys-combined-contact-form .contact-form-submit:hover:not(:disabled) {
          background: #1F2937;
        }

        .allys-combined-contact-form .contact-form-submit:active:not(:disabled) {
          background: #030712;
        }

        .allys-combined-contact-form .contact-form-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .allys-combined-contact-form .contact-form-success {
          margin-top: 1.5rem;
          padding: 1rem;
          background: #F0FDF4;
          color: #166534;
          border: 1px solid #BBF7D0;
          border-radius: 0.5rem;
          text-align: center;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 0.875rem;
        }

        .allys-combined-contact-form .contact-form-error-message {
          margin-top: 1.5rem;
          padding: 1rem;
          background: #FEF2F2;
          color: #991B1B;
          border: 1px solid #FECACA;
          border-radius: 0.5rem;
          text-align: center;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 0.875rem;
        }
      `}</style>
    </section>
  );
});

